import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { useCatalog, findPackageById, findPackageByName, getAddonsForPackage, computePricing } from "@/lib/CatalogProvider";

import { InquiryShell } from "@/components/inquiry/InquiryShell";
import { InquiryStepper } from "@/components/inquiry/InquiryStepper";
import {
    emptyInquiryState,
    loadInquiryState,
    persistInquiryState,
    clearInquiryState,
    PACKAGE_STEPS,
    CUSTOM_STEPS,
    validateStep,
} from "@/components/inquiry/inquiryState";
import { resolveServiceLabel } from "@/components/inquiry/inquiryServices";

import StepRoute from "@/components/inquiry/steps/StepRoute";
import StepPackage from "@/components/inquiry/steps/StepPackage";
import StepAddons from "@/components/inquiry/steps/StepAddons";
import StepBrand from "@/components/inquiry/steps/StepBrand";
import StepVision from "@/components/inquiry/steps/StepVision";
import StepTimeline from "@/components/inquiry/steps/StepTimeline";
import StepBudget from "@/components/inquiry/steps/StepBudget";
import StepServices from "@/components/inquiry/steps/StepServices";
import StepContact from "@/components/inquiry/steps/StepContact";
import StepReview from "@/components/inquiry/steps/StepReview";
import StepSuccess from "@/components/inquiry/steps/StepSuccess";
import { trackEvent } from "@/analytics/ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * InquiryFlow — the brain of the multi-step consultation experience.
 *
 * Modes
 * -----
 * - "package"  → 8-step guided Package Journey.
 * - "custom"   → 7-step guided Custom Project Journey.
 *
 * Entry modes
 * -----------
 * - Route step (default when hitting /start).
 * - Package pre-selected (deep link `/start/package/:id` from a package CTA).
 * - Custom (deep link `/start/custom` — lands directly on Services step).
 */
export const InquiryFlow = ({
    mode = "package",           // "package" | "custom"
    initialStepId = "route",    // Which step to land on.
    initialPackage = null,       // Pre-selected package (deep link).
    initialInquiryType = "branding", // "branding" | "video" (theme hint)
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { packages, addons } = useCatalog();

    // ---------- state ----------
    const [data, setData] = useState(() => {
        const restored = loadInquiryState();
        const base = restored || emptyInquiryState();
        return {
            ...base,
            inquiry_kind: mode === "custom" ? "custom" : (base.inquiry_kind || "package"),
            inquiry_type: initialInquiryType || base.inquiry_type || "branding",
        };
    });

    // Adaptive step registry based on current journey.
    const steps = useMemo(
        () => (data.inquiry_kind === "custom" ? CUSTOM_STEPS : PACKAGE_STEPS),
        [data.inquiry_kind]
    );
    const initialIdx = Math.max(0, steps.findIndex((s) => s.id === initialStepId));

    // `activeIndex` tracks the current step; `highestCompletedIndex` tracks
    // the furthest a user has reached (they can jump back but not skip ahead).
    const [activeIndex, setActiveIndex] = useState(initialIdx);
    const [highestCompletedIndex, setHighestCompletedIndex] = useState(initialIdx);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [successId, setSuccessId] = useState(null);

    // ---------- persistence ----------
    useEffect(() => {
        persistInquiryState(data);
    }, [data]);

    // Analytics: the visitor has entered the inquiry flow (fires once).
    useEffect(() => {
        trackEvent("inquiry_start", { source: mode === "custom" ? "custom" : "package" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync activeIndex to browser back/forward via location.state.
    useEffect(() => {
        const idx = location.state?.stepIndex;
        if (typeof idx === "number" && idx >= 0 && idx < steps.length && idx !== activeIndex) {
            setActiveIndex(idx);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.stepIndex]);

    // ---------- deep-link package pre-selection ----------
    // If we enter with a specific package deep-link, seed the form once the
    // catalog has resolved. We never overwrite an existing user selection.
    const pkgSeededFor = useRef(null);
    useEffect(() => {
        if (!initialPackage || mode !== "package") return;
        const sessionKey = `${initialPackage.id || initialPackage.name}`;
        if (pkgSeededFor.current === sessionKey) return;

        let pkg = null;
        if (initialPackage.id) pkg = findPackageById(packages, initialPackage.id);
        if (!pkg && initialPackage.name) pkg = findPackageByName(packages, initialPackage.name);
        if (!pkg) return;

        // Only seed if the current selection differs
        setData((d) => {
            if (d.selected_package === pkg.name && d.package_id === pkg.id) return d;
            return {
                ...d,
                inquiry_kind: "package",
                inquiry_type: pkg.kind === "video" ? "video" : "branding",
                selected_package: pkg.name,
                package_id: pkg.id,
                package_category: pkg.category || "",
                package_kind: pkg.kind || "",
                package_price: pkg.price_label || pkg.priceLabel || "",
                base_price: Number(pkg.base_price || 0),
                is_custom_scope: false,
                // Drop any addons that don't apply to this package.
                addons: (d.addons || []).filter((id) =>
                    getAddonsForPackage(addons, pkg).some((a) => a.id === id)
                ),
            };
        });
        pkgSeededFor.current = sessionKey;
    }, [initialPackage, packages, addons, mode]);

    // ---------- helpers ----------
    const setField = useCallback((key, value) => {
        setData((d) => ({ ...d, [key]: value }));
    }, []);

    const canGoNext = useMemo(() => {
        const currentId = steps[activeIndex]?.id;
        const stepErr = validateStep(currentId, data);
        return Object.keys(stepErr).length === 0;
    }, [activeIndex, data, steps]);

    const goToStep = useCallback((idx, options = {}) => {
        if (idx < 0 || idx >= steps.length) return;
        const currentId = steps[activeIndex]?.id;

        // Moving forward: validate the current step first (unless it's a free
        // stepper jump, which is allowed to land anywhere by number).
        if (idx > activeIndex && !options.force) {
            const stepErr = validateStep(currentId, data);
            if (Object.keys(stepErr).length > 0) {
                setErrors(stepErr);
                if (stepErr._step) {
                    toast.error(stepErr._step);
                } else {
                    toast.error("Please complete the required fields to continue.");
                }
                return;
            }
        }

        setErrors({});
        setActiveIndex(idx);
        setHighestCompletedIndex((cur) => Math.max(cur, idx));

        // Push a history entry so the browser Back button navigates naturally.
        if (options.pushHistory !== false) {
            const nextPath = idx === 0 ? "/start" : location.pathname;
            navigate(nextPath, { state: { stepIndex: idx }, replace: false });
        }
    }, [activeIndex, data, steps, location.pathname, navigate]);

    const goNext = useCallback(() => goToStep(activeIndex + 1), [activeIndex, goToStep]);
    const goBack = useCallback(() => goToStep(activeIndex - 1, { pushHistory: true }), [activeIndex, goToStep]);

    // ---------- auto-advance on package selection ----------
    // Driven imperatively from `onPackageSelect` so the advance only ever fires
    // when the user *actively* picks a package. This means:
    //   - First selection ever → advances after 320ms.
    //   - Navigating BACK to the Package step with an existing selection → NO advance.
    //   - Picking a *different* package after returning → advances again.
    // This behaviour is required by the Package Inquiry UX (PRD §12).
    const packageAutoAdvanceRef = useRef(null);
    useEffect(() => {
        // Ensure any queued advance is cancelled if the user leaves the package step.
        return () => {
            if (packageAutoAdvanceRef.current) clearTimeout(packageAutoAdvanceRef.current);
        };
    }, []);

    // ---------- Package preselection chip helper ----------
    const changePackage = useCallback(() => {
        setErrors({});
        const pkgIdx = steps.findIndex((s) => s.id === "package");
        if (pkgIdx >= 0) goToStep(pkgIdx);
    }, [steps, goToStep]);

    // ---------- step handlers ----------
    const onRouteSelect = (kind) => {
        setData((d) => ({ ...d, inquiry_kind: kind }));
        setErrors({});
        // Both journeys advance to their second step (index 1) from Route.
        setActiveIndex(1);
        setHighestCompletedIndex((cur) => Math.max(cur, 1));
        const nextPath = kind === "custom" ? "/start/custom" : "/start/package";
        navigate(nextPath, { state: { stepIndex: 1 }, replace: false });
    };

    const onPackageSelect = (pkg) => {
        setData((d) => {
            // If the user picks the exact same package they already had selected,
            // treat it as a re-confirmation (still advance) but avoid churning
            // otherwise-untouched fields.
            const samePkg = d.package_id === pkg.id && d.selected_package === pkg.name;
            const nextAddons = (d.addons || []).filter((id) =>
                getAddonsForPackage(addons, pkg).some((a) => a.id === id)
            );
            return {
                ...d,
                inquiry_kind: "package",
                inquiry_type: pkg.kind === "video" ? "video" : "branding",
                selected_package: pkg.name,
                package_id: pkg.id,
                package_category: pkg.category || "",
                package_kind: pkg.kind || "",
                package_price: pkg.price_label || pkg.priceLabel || "",
                base_price: Number(pkg.base_price || 0),
                is_custom_scope: false,
                // Revalidate add-ons: drop any that no longer apply.
                addons: samePkg ? d.addons : nextAddons,
            };
        });
        // Imperative auto-advance — only fires on a real user click, never on
        // re-render, hydration, or back-navigation to the package step.
        if (packageAutoAdvanceRef.current) clearTimeout(packageAutoAdvanceRef.current);
        packageAutoAdvanceRef.current = setTimeout(() => {
            // Only advance if the user is still on the package step.
            if (steps[activeIndex]?.id === "package") {
                goToStep(activeIndex + 1);
            }
        }, 320);
    };

    const onPackageSelectCustom = () => {
        // Redirect to the real Custom Project Journey instead of tucking a custom scope
        // inside the Package flow. Clearer separation of intent.
        setData((d) => ({ ...d, inquiry_kind: "custom", is_custom_scope: false }));
        setErrors({});
        setActiveIndex(1); // Services step
        setHighestCompletedIndex((cur) => Math.max(cur, 1));
        navigate("/start/custom", { state: { stepIndex: 1 }, replace: false });
    };

    const onToggleAddon = (id) => {
        setData((d) => {
            const has = (d.addons || []).includes(id);
            return { ...d, addons: has ? d.addons.filter((a) => a !== id) : [...(d.addons || []), id] };
        });
    };

    const onToggleService = (id) => {
        setData((d) => {
            const list = d.services_needed || [];
            const has = list.includes(id);
            return { ...d, services_needed: has ? list.filter((s) => s !== id) : [...list, id] };
        });
    };

    // ---------- coupon ----------
    const onApplyCoupon = useCallback((code, percent) => {
        setData((d) => ({ ...d, coupon_code: (code || "").trim().toUpperCase(), coupon_percent: Number(percent) || 0 }));
    }, []);
    const onRemoveCoupon = useCallback(() => {
        setData((d) => ({ ...d, coupon_code: "", coupon_percent: 0 }));
    }, []);

    // ---------- submit ----------
    const submit = async () => {
        // Composite validation
        const allErr = validateStep("review", data);
        if (Object.keys(allErr).length > 0) {
            setErrors(allErr);
            toast.error("Please complete the required fields before sending.");
            return;
        }
        setSubmitError(null);
        setSubmitting(true);

        const isCustom = data.inquiry_kind === "custom";

        // Package snapshot (only meaningful for Package journey).
        const selectedPkg = !isCustom && !data.is_custom_scope
            ? findPackageByName(packages, data.selected_package)
            : null;
        const availableAddons = selectedPkg ? getAddonsForPackage(addons, selectedPkg) : [];
        const selectedAddonObjs = availableAddons.filter((a) => (data.addons || []).includes(a.id));
        const pricing = computePricing({
            selectedPackage: selectedPkg,
            selectedAddons: selectedAddonObjs,
            isCustomScope: data.is_custom_scope,
        });

        const finalBudget =
            data.budget_mode === "custom" && data.custom_budget
                ? `Custom: ${data.custom_budget}`
                : data.estimated_budget || undefined;

        // Coupon / discount — Package journey with a real fixed price only.
        const estTotal = Number(pricing.estimatedTotal || 0);
        const couponPercent = (!isCustom && !data.is_custom_scope && data.coupon_code)
            ? Math.max(0, Math.min(Number(data.coupon_percent) || 0, 100))
            : 0;
        const discountAmount = couponPercent > 0 ? Math.round(estTotal * couponPercent) / 100 : 0;
        const finalTotal = Math.round((estTotal - discountAmount) * 100) / 100;

        // Human-readable services list for admin/email context.
        const servicesLabels = (data.services_needed || []).map(resolveServiceLabel);

        // Package Journey: package IS the investment — never send a budget.
        // Custom Journey: budget is the primary financial signal.
        const payload = {
            inquiry_kind: data.inquiry_kind || "package",
            inquiry_type: isCustom ? "branding" : (data.inquiry_type || (selectedPkg?.kind === "video" ? "video" : "branding")),

            full_name: (data.full_name || "").trim(),
            business_name: (data.business_name || "").trim() || undefined,
            business_type: (data.business_type || "").trim() || undefined,
            website_link: (data.website_link || "").trim() || undefined,
            facebook_link: (data.facebook_link || "").trim() || undefined,
            instagram_link: (data.instagram_link || "").trim() || undefined,

            // Package-specific fields (undefined for custom journey)
            selected_package: isCustom ? undefined : (data.selected_package || undefined),
            package_id: isCustom ? undefined : (data.package_id || undefined),
            package_category: isCustom ? undefined : (data.package_category || undefined),
            package_price: isCustom ? undefined : (data.package_price || undefined),
            package_kind: isCustom ? undefined : (data.package_kind || undefined),
            base_price: isCustom || data.is_custom_scope ? null : Number(pricing.basePrice || 0),
            is_custom_scope: !!data.is_custom_scope,

            addons: isCustom ? [] : selectedAddonObjs.map((a) => ({ id: a.id, name: a.name, price: Number(a.price) })),
            addons_total: isCustom ? 0 : Number(pricing.addOnsTotal || 0),
            estimated_total: isCustom || data.is_custom_scope ? null : Number(pricing.estimatedTotal || 0),

            // Coupon / discount (Package journey only; backend recomputes authoritative totals)
            coupon_code: isCustom || data.is_custom_scope ? undefined : (data.coupon_code || undefined),
            discount_percent: isCustom || data.is_custom_scope ? undefined : (couponPercent || undefined),
            discount_amount: isCustom || data.is_custom_scope ? undefined : (discountAmount || undefined),
            final_total: isCustom || data.is_custom_scope ? undefined : finalTotal,

            // Custom-specific fields
            services_needed: isCustom ? servicesLabels : [],
            budget_range: isCustom ? finalBudget : undefined,

            // Budget only travels with Custom Journey submissions — Package
            // inquiries deliberately omit these because the package price is
            // the authoritative investment signal.
            estimated_budget: isCustom ? finalBudget : undefined,
            custom_budget: isCustom && data.budget_mode === "custom" ? (data.custom_budget || undefined) : undefined,
            expected_timeline: data.expected_timeline || undefined,

            goals: (data.goals || "").trim() || undefined,
            what_do_you_need: (data.what_do_you_need || "").trim() || undefined,
            project_details: (data.project_details || "").trim() || undefined,

            preferred_communication: data.preferred_communication || undefined,
            whatsapp_number: (data.whatsapp_number || "").trim() || undefined,
            instagram_username: (data.instagram_username || "").trim() || undefined,
            email: (data.email || "").trim(),
            detected_country: data.detected_country || undefined,
            detected_country_iso: data.detected_country_iso || undefined,
        };

        try {
            const res = await axios.post(`${API}/inquiry`, payload, { timeout: 25000 });
            const id = res.data?.id || null;
            setSuccessId(id);
            trackEvent("inquiry_complete", { form: isCustom ? "custom" : "package" });
            clearInquiryState();
            toast.success("Inquiry received", {
                description: "Pranto will personally review your brief and respond within 24h.",
            });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
            setSubmitError(msg || "Could not submit. Please try again or reach out via WhatsApp.");
            toast.error("Could not submit", { description: msg || "Please try again." });
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- render ----------
    if (successId) {
        return (
            <InquiryShell leftRail={null} dataTestId="inquiry-shell-success">
                <StepSuccess
                    inquiryId={successId}
                    onStartAnother={() => {
                        setSuccessId(null);
                        setData(emptyInquiryState());
                        setHighestCompletedIndex(0);
                        setActiveIndex(0);
                        navigate("/start", { replace: true });
                    }}
                />
            </InquiryShell>
        );
    }

    const activeId = steps[activeIndex]?.id;

    const renderStep = () => {
        switch (activeId) {
            case "route":
                return <StepRoute data={data} onSelect={onRouteSelect} />;
            case "package":
                return (
                    <StepPackage
                        data={data}
                        onSelect={onPackageSelect}
                        onSelectCustom={onPackageSelectCustom}
                    />
                );
            case "addons":
                return <StepAddons data={data} onToggleAddon={onToggleAddon} />;
            case "services":
                return <StepServices data={data} onToggleService={onToggleService} errors={errors} />;
            case "brand":
                return <StepBrand data={data} onField={setField} />;
            case "vision":
                return <StepVision data={data} onField={setField} errors={errors} />;
            case "timeline":
                return <StepTimeline data={data} onField={setField} errors={errors} />;
            case "budget":
                return <StepBudget data={data} onField={setField} errors={errors} />;
            case "contact":
                return <StepContact data={data} onField={setField} errors={errors} />;
            case "review":
                return (
                    <StepReview
                        data={data}
                        onEdit={(idx) => goToStep(idx)}
                        onSubmit={submit}
                        submitting={submitting}
                        submitError={submitError}
                        errors={errors}
                        onApplyCoupon={onApplyCoupon}
                        onRemoveCoupon={onRemoveCoupon}
                    />
                );
            default:
                return null;
        }
    };

    // Route step has its own CTA. Review has its own submit button.
    const showFooterNav = activeId !== "route" && activeId !== "review";

    // Show a persistent "You selected X · Change Package" chip on steps 3–7
    // of the Package journey (Add-ons through Contact) when a package is set.
    const showPackageChip =
        data.inquiry_kind === "package" &&
        (data.selected_package || data.is_custom_scope) &&
        ["addons", "brand", "vision", "timeline", "contact"].includes(activeId);

    return (
        <InquiryShell
            leftRail={
                <InquiryStepper
                    steps={steps}
                    activeIndex={activeIndex}
                    highestCompletedIndex={highestCompletedIndex}
                    onJump={(idx) => goToStep(idx, { force: true })}
                    onBack={goBack}
                />
            }
        >
            {showPackageChip && (
                <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-6 inline-flex flex-wrap items-center gap-3 rounded-full border px-3 py-2"
                    style={{
                        borderColor: "color-mix(in srgb, var(--pg-accent) 35%, var(--pg-border))",
                        background: "color-mix(in srgb, var(--pg-accent) 6%, var(--pg-surface))",
                    }}
                    data-testid="inquiry-package-chip"
                >
                    <span
                        className="text-[11px] font-medium tracking-normal"
                        style={{ color: "var(--pg-fg-muted)" }}
                    >
                        You selected
                    </span>
                    <span
                        className="font-display text-sm"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="inquiry-package-chip-name"
                    >
                        {data.is_custom_scope ? "Custom Project" : data.selected_package}
                    </span>
                    {!data.is_custom_scope && data.package_price && (
                        <span className="pg-price-num text-sm pg-text-gold-static">
                            {data.package_price}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={changePackage}
                        data-testid="inquiry-package-chip-change"
                        className="text-[11px] font-medium tracking-normal underline-offset-4 hover:underline"
                        style={{ color: "var(--pg-accent)" }}
                    >
                        Change Package
                    </button>
                </motion.div>
            )}

            <AnimatePresence mode="wait">
                <motion.div
                    key={activeId}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    data-testid={`inquiry-step-${activeId}`}
                >
                    {renderStep()}
                </motion.div>
            </AnimatePresence>

            {showFooterNav && (
                <div
                    className="mt-12 flex flex-col-reverse gap-3 border-t pt-8 sm:flex-row sm:items-center sm:justify-between"
                    style={{ borderColor: "var(--pg-border)" }}
                    data-testid="inquiry-footer-nav"
                >
                    <button
                        type="button"
                        onClick={goBack}
                        disabled={activeIndex === 0}
                        className="pg-btn-ghost disabled:cursor-not-allowed disabled:opacity-40"
                        data-testid="inquiry-back-step"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={1.6} />
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={goNext}
                        disabled={!canGoNext}
                        className="pg-btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid="inquiry-next-step"
                    >
                        Continue
                        <ArrowRight className="h-4 w-4" strokeWidth={1.6} />
                    </button>
                </div>
            )}
        </InquiryShell>
    );
};

export default InquiryFlow;
